/**
 * UniversalUpload.jsx
 * 
 * Universal document upload component for the Document Intake Agent.
 * Supports any document type with drag-and-drop interface.
 */

import React, { useState, useRef } from 'react';
import './UniversalUpload.css';

const UniversalUpload = ({ auditorId, engagementId, onUploadComplete, onError }) => {
  const [files, setFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const supportedTypes = {
    'application/pdf': 'PDF Document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
    'application/msword': 'Word Document (Legacy)',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
    'application/vnd.ms-excel': 'Excel Spreadsheet (Legacy)',
    'text/csv': 'CSV File',
    'text/plain': 'Text File',
    'image/png': 'PNG Image',
    'image/jpeg': 'JPEG Image',
    'image/tiff': 'TIFF Image'
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(file => {
      // Basic validation
      if (file.size > 50 * 1024 * 1024) { // 50MB limit
        onError(`File "${file.name}" is too large. Maximum size is 50MB.`);
        return false;
      }
      return true;
    });

    setFiles(prevFiles => [...prevFiles, ...validFiles]);
  };

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeLabel = (mimeType) => {
    return supportedTypes[mimeType] || 'Unknown File Type';
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      onError('Please select at least one file to upload.');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append('auditor_id', auditorId);
      formData.append('engagement_id', engagementId);
      
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch('/api/v1/document-intake/upload', {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        onUploadComplete({
          job_id: result.job_id,
          message: result.message,
          data: result.data
        });
        setFiles([]); // Clear files after successful upload
      } else {
        throw new Error(result.message || 'Upload failed');
      }
    } catch (error) {
      onError(`Upload failed: ${error.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="universal-upload">
      <div className="upload-header">
        <h2>Upload Documents</h2>
        <p>Upload any type of document for universal structure extraction and processing.</p>
      </div>

      {/* Drag and Drop Zone */}
      <div 
        className={`upload-zone ${isDragOver ? 'drag-over' : ''}`}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div className="upload-zone-content">
          <div className="upload-icon">📁</div>
          <div className="upload-text">
            <h3>Drag and drop your documents here</h3>
            <p>or <span className="upload-link">click to browse</span></p>
          </div>
          <div className="upload-info">
            <p>Supports: PDF, Word, Excel, CSV, Images, and more</p>
            <p>Maximum file size: 50MB per file</p>
          </div>
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.png,.jpg,.jpeg,.tiff"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {/* File List */}
      {files.length > 0 && (
        <div className="file-list">
          <h3>Selected Files ({files.length})</h3>
          <div className="file-items">
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-icon">📄</div>
                <div className="file-details">
                  <div className="file-name">{file.name}</div>
                  <div className="file-meta">
                    <span>{getFileTypeLabel(file.type)}</span>
                    <span>•</span>
                    <span>{formatFileSize(file.size)}</span>
                  </div>
                </div>
                <button 
                  className="file-remove"
                  onClick={() => removeFile(index)}
                  disabled={isUploading}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Actions */}
      <div className="upload-actions">
        <button 
          className="upload-button"
          onClick={handleUpload}
          disabled={files.length === 0 || isUploading}
        >
          {isUploading ? (
            <>
              <span className="loading-spinner">⏳</span>
              Uploading...
            </>
          ) : (
            <>
              Upload {files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''}` : 'Documents'}
            </>
          )}
        </button>
        
        {files.length > 0 && (
          <button 
            className="clear-button"
            onClick={() => setFiles([])}
            disabled={isUploading}
          >
            Clear All
          </button>
        )}
      </div>

      {/* Supported File Types */}
      <div className="supported-types">
        <h4>Supported File Types:</h4>
        <div className="type-grid">
          <div className="type-category">
            <strong>Documents:</strong>
            <span>PDF, Word (.docx, .doc), Text files</span>
          </div>
          <div className="type-category">
            <strong>Spreadsheets:</strong>
            <span>Excel (.xlsx, .xls), CSV</span>
          </div>
          <div className="type-category">
            <strong>Images:</strong>
            <span>PNG, JPEG, TIFF (with OCR)</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversalUpload;
